import DatabaseModel from './DatabaseModel.js';
import { unwrapDto } from './responseUtils.js';

// Tarjeta «Puesto y funciones» de la ficha del empleado (#51).
// Endpoint propio y no el de empleados: nada de esto se guarda en EmpleadoMO, así que guardar la
// ficha, cambiarle el rol o sincronizar con SAP no puede borrar ninguna función (I5).
const RhEmpleadoFunciones = Object.create(DatabaseModel);

RhEmpleadoFunciones.model = 'rh/empleado-funciones';
RhEmpleadoFunciones.modelFront = 'rh-empleado-funciones';

// GET /movilidad/rh/empleado-funciones/{username}
// → { username, nombre, vigentes[], historico[], funcionesDisponibles[], puestosDisponibles[],
//     editable }
//
// `vigentes` son los tramos sin `hasta`, la principal primero; `historico` es TODO lo que ha
// desempeñado, vigente o no, lo más reciente primero. Cada fila lleva las dos lecturas del nombre
// y del puesto a propósito: `funcionNombre`/`puestoNombre` son los de hoy (los que pinta la tabla
// de arriba) y `nombreFuncionAlAlta`/`nombrePuestoAlAlta` la foto del día del alta, que es lo que
// pinta el histórico para que recolocar una función no reescriba el pasado.
//
// `funcionesDisponibles` y `puestosDisponibles` llegan vacíos cuando `editable` es false: el
// propio empleado ve sus funciones pero ni el desplegable de «Añadir función» ni la ventana de
// «Añadir funciones de un puesto…» son para él.
//
// `puestosDisponibles` son TODOS los puestos activos, también los que hoy no tienen ninguna
// función: elegir uno así es uno de los casos que la ventana tiene que contar, y derivar la lista
// de `funcionesDisponibles` lo dejaría fuera del desplegable sin decir por qué.
RhEmpleadoFunciones.get = function (username) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(username),
        method: 'GET'
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/empleado-funciones/{username} → la tarjeta ya con la función añadida.
// `motivo` es obligatorio; el backend lo rechaza si falta. Marcar `principal` cuando ya hay otra
// principal vigente devuelve un 409 que nombra la función en conflicto: no se arregla solo.
RhEmpleadoFunciones.anadir = function (username, funcion) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(username),
        method: 'POST',
        data: funcion
    }, false).then(unwrapDto);
};

// GET /movilidad/rh/empleado-funciones/{username}/puestos/{puestoId}/funciones
// → { puestoId, puestoCodigo, puestoNombre,
//     funciones: [{ codigoInterno, codigo, nombre, yaVigente, desdeVigente }] }
//
// Los DOS códigos, y cada uno hace lo suyo (#483): `codigoInterno` (`FN-0007`) es la identidad
// —lo que se manda de vuelta al confirmar— y `codigo` (`F-DIR-01`) es lo que se lee en la fila.
// El visible se puede reordenar, así que no vale como referencia.
//
// Lo que necesita la ventana «Añadir funciones de un puesto…» (#485). Solo las funciones ACTIVAS
// del puesto: las desactivadas no se pueden asignar a nadie y no viajan. `yaVigente` lo decide el
// servidor sobre la PERSONA —el grupo de usuarios vinculados por «Misma persona que»—, no sobre el
// usuario de la ficha, así que no se puede recalcular aquí cruzando `vigentes`.
//
// Las dos maneras de que no haya nada que incorporar se distinguen sin más datos: `funciones`
// vacío es «el puesto no tiene funciones activas» y `funciones` entero con `yaVigente` es «ya las
// desempeña todas».
RhEmpleadoFunciones.funcionesDePuesto = function (username, puestoId) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(username)
            + '/puestos/' + encodeURIComponent(puestoId) + '/funciones',
        method: 'GET'
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/empleado-funciones/{username}/puesto → la tarjeta ya con las funciones
// añadidas. Cuerpo: { puestoId, desde, motivo, codigosFuncion: [] }, con los códigos INTERNOS de
// las marcadas, igual que el `codigoFuncion` del alta de una en una (#483).
//
// `desde` y `motivo` valen para TODAS las marcadas y el motivo es obligatorio, igual que en el
// alta de una en una. Es TODO O NADA: si una choca, no se incorpora ninguna, así que un error aquí
// deja la ficha exactamente como estaba. Ninguna entra marcada como función principal.
RhEmpleadoFunciones.anadirDePuesto = function (username, datos) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(username)
            + '/puesto',
        method: 'POST',
        data: datos
    }, false).then(unwrapDto);
};

// POST /movilidad/rh/empleado-funciones/{username}/{id}/finalizar → la tarjeta ya actualizada.
// `hasta` y `motivo` son los dos obligatorios. No borra nada: el tramo se queda en el histórico.
RhEmpleadoFunciones.finalizar = function (username, id, datos) {
    const self = this;
    return self.sendRequest({
        url: self.server_movilidad + self.prefix + self.model + '/' + encodeURIComponent(username)
            + '/' + encodeURIComponent(id) + '/finalizar',
        method: 'POST',
        data: datos
    }, false).then(unwrapDto);
};

export default RhEmpleadoFunciones;
